import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const cfg = { withCredentials: true };
const errMsg = (e, fb) => e.response?.data?.detail || fb;
const fieldCls = "bg-bt-surface-2 border-bt-border-subtle text-bt-text-primary font-mono text-sm";
const EMPTY = { email: "", name: "", password: "", role: "user", send_welcome: false };

export const AddUserDialog = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 4) { toast.error("Password must be at least 4 characters"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${BACKEND}/api/admin/users`, form, cfg);
      onCreated(data);
      toast.success(`User created: ${data.email}`);
      if (data.welcome_status === "sent") toast.success("Welcome email sent");
      else if (data.welcome_status === "skipped") toast.warning("Welcome email not sent: SMTP not configured");
      else if (data.welcome_status === "failed") toast.error("Welcome email failed to send");
      setForm(EMPTY);
      setOpen(false);
    } catch (e2) { toast.error(errMsg(e2, "Failed to create user")); }
    setSaving(false);
  };

  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)} data-testid="add-user-btn"
        className="h-7 px-3 text-[11px] font-mono bg-bt-gold text-black hover:bg-bt-gold/80">
        <UserPlus className="w-3.5 h-3.5 mr-1" /> Add User
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-bt-surface border-bt-border-subtle text-bt-text-primary" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono">Add User</DialogTitle>
            <DialogDescription className="text-bt-text-tertiary">The user can log in immediately with these credentials.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div><Label className="text-xs font-mono text-bt-text-secondary">Email</Label>
              <Input type="email" required value={form.email} onChange={set("email")} className={fieldCls} data-testid="add-user-email" /></div>
            <div><Label className="text-xs font-mono text-bt-text-secondary">Name</Label>
              <Input value={form.name} onChange={set("name")} className={fieldCls} data-testid="add-user-name" /></div>
            <div><Label className="text-xs font-mono text-bt-text-secondary">Password</Label>
              <Input type="password" required value={form.password} onChange={set("password")} className={fieldCls} data-testid="add-user-password" /></div>
            <div><Label className="text-xs font-mono text-bt-text-secondary">Role</Label>
              <select value={form.role} onChange={set("role")} data-testid="add-user-role"
                className={`w-full h-9 px-2 border ${fieldCls}`}>
                <option value="user">user</option>
                <option value="superadmin">superadmin</option>
              </select></div>
            <label className="flex items-center gap-2 text-xs font-mono text-bt-text-secondary cursor-pointer">
              <input type="checkbox" checked={form.send_welcome} data-testid="add-user-send-welcome"
                onChange={(e) => setForm(f => ({ ...f, send_welcome: e.target.checked }))} className="accent-[#eab308]" />
              Send welcome email (includes login link + this password)
            </label>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="add-user-cancel">Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-bt-gold text-black hover:bg-bt-gold/80" data-testid="add-user-submit">
                {saving && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Create User
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const RoleSelect = ({ u, isSelf, onChanged }) => {
  const badge = u.role === "superadmin"
    ? "bg-bt-gold/15 text-bt-gold border-bt-gold/30"
    : "bg-bt-surface-2 text-bt-text-secondary border-bt-border-subtle";
  if (isSelf) return <span className={`inline-flex px-2 py-0.5 text-xs font-mono font-medium border ${badge}`} data-testid={`role-${u.email}`}>{u.role}</span>;
  const change = async (e) => {
    const role = e.target.value;
    if (role === u.role) return;
    if (!window.confirm(`Change ${u.email} role to "${role}"?`)) return;
    try {
      await axios.put(`${BACKEND}/api/admin/users/${encodeURIComponent(u.email)}/role`, { role }, cfg);
      onChanged(role);
      toast.success(`${u.email} is now ${role}`);
    } catch (e2) { toast.error(errMsg(e2, "Role change failed")); }
  };
  return (
    <select value={u.role} onChange={change} data-testid={`role-select-${u.email}`}
      className={`px-1.5 py-0.5 text-xs font-mono font-medium border cursor-pointer outline-none ${badge}`}>
      <option value="user">user</option>
      <option value="superadmin">superadmin</option>
    </select>
  );
};

export const DeleteUserButton = ({ u, onDeleted }) => {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await axios.delete(`${BACKEND}/api/admin/users/${encodeURIComponent(u.email)}`, cfg);
      onDeleted();
      toast.success(`Deleted ${u.email} and all their data`);
    } catch (e) { toast.error(errMsg(e, "Delete failed")); }
    setBusy(false);
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="sm" disabled={busy} data-testid={`delete-user-${u.email}`} title="Delete user"
          className="h-7 px-2 text-[10px] font-mono text-bt-negative hover:bg-bt-negative/10">
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-bt-surface border-bt-border-subtle text-bt-text-primary">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-mono">Delete {u.email}?</AlertDialogTitle>
          <AlertDialogDescription className="text-bt-text-tertiary">
            This permanently removes the account and all of its data: portfolio, watchlist, trade history, limit orders, Woodrow account, trades and snapshots. This cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="delete-user-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={remove} className="bg-bt-negative text-white hover:bg-bt-negative/80" data-testid="delete-user-confirm">
            Delete permanently
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

